import React, { useState, useEffect } from "react"
import {
    Box,
    Typography,
    Button,
    IconButton,
    Paper,
    CircularProgress,
    TextField,
    Chip,
} from "@mui/material"
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined"
import AddIcon from "@mui/icons-material/Add"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined"
import ShareIcon from "@mui/icons-material/Share"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import WorkerModal from "../EditWorkedModal/WorkerModal"
import CreateRosterModal from "../CreateRosterModal/CreateRosterModal"
import WorkerSearch from "../WorkerSearch/WorkerSearch"
import WorkerScheduleModal from "../WorkerScheduleModal/WorkerScheduleModal"
import { createProjectRoster, getRosterDetails, addWorkersToRoster, deleteWorkerFromRoster, updateRoster, updateWorkerInRoster, submitRosterForApproval, revertRosterToDraft } from "../../../utils/RosterAPI"
import { toast } from "react-toastify"

export default function RosterTab({ projectId, rosterId, onRosterCreated }) {
    const [roster, setRoster] = useState(null)
    const [loading, setLoading] = useState(false)
    const [selectedWorker, setSelectedWorker] = useState(null)
    const [openEdit, setOpenEdit] = useState(false)
    const [openCreateRoster, setOpenCreateRoster] = useState(false)
    const [isEditingRosterName, setIsEditingRosterName] = useState(false)
    const [editedRosterName, setEditedRosterName] = useState("")
    const [selectedWorkerForSchedule, setSelectedWorkerForSchedule] = useState(null)
    const [openWorkerSchedule, setOpenWorkerSchedule] = useState(false)
    const [uploadingCSV, setUploadingCSV] = useState(false)
    const [submittingRoster, setSubmittingRoster] = useState(false)
    const fileInputRef = React.useRef(null)

    // Track if changes were made after approval
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

    // Roster status helpers
    const rosterStatus = roster?.status?.toUpperCase() || "DRAFT"
    const isApproved = rosterStatus === "APPROVED"
    const isSubmitted = rosterStatus === "SUBMITTED"
    const isRejected = rosterStatus === "REJECTED"
    const isDraft = rosterStatus === "DRAFT" || rosterStatus === "CREATED"
    
    // Roster is only locked when pending approval (SUBMITTED)
    const isLocked = isSubmitted
    
    // Show re-submit button when approved roster has changes OR for draft/rejected
    const needsResubmit = (isApproved && hasUnsavedChanges) || isDraft || isRejected

    // Get status chip styling
    const getStatusChipProps = () => {
        if (isApproved && hasUnsavedChanges) {
            return { label: "Changes Pending", sx: { backgroundColor: "#FFF6ED", color: "#C4320A", fontWeight: 500 } }
        }
        switch (rosterStatus) {
            case "APPROVED":
                return { label: "Approved", sx: { backgroundColor: "#ECFDF3", color: "#027A48", fontWeight: 500 } }
            case "SUBMITTED":
                return { label: "Pending Approval", sx: { backgroundColor: "#FFF6ED", color: "#C4320A", fontWeight: 500 } }
            case "REJECTED":
                return { label: "Rejected", sx: { backgroundColor: "#FEF3F2", color: "#B42318", fontWeight: 500 } }
            default:
                return { label: "Draft", sx: { backgroundColor: "#F2F4F7", color: "#344054", fontWeight: 500 } }
        }
    }

    // Fetch roster details on mount or when rosterId changes
    useEffect(() => {
        if (rosterId) {
            fetchRosterDetails(true) // Reset changes flag on initial load
        } else {
            // If no rosterId, clear roster state
            setRoster(null)
            setHasUnsavedChanges(false)
        }
    }, [rosterId])

    const fetchRosterDetails = async (resetChangesFlag = false) => {
        if (!rosterId) return
        
        setLoading(true)
        try {
            const response = await getRosterDetails(rosterId)
            // API returns roster object directly (not wrapped in data)
            if (response) {
                setRoster(response)
                // Only reset changes flag on initial load or after submission
                if (resetChangesFlag) {
                    setHasUnsavedChanges(false)
                }
            }
        } catch (error) {
            console.error("Error fetching roster:", error)
            toast.error(error?.message || "Failed to fetch roster details")
        } finally {
            setLoading(false)
        }
    }

    const handleCreateRoster = async (rosterName) => {
        if (!projectId) {
            toast.error("Project ID is missing")
            return
        }

        try {
            const response = await createProjectRoster(projectId, rosterName)
            if (response?.data || response) {
                const newRoster = response.data || response
                setRoster(newRoster)
                setOpenCreateRoster(false)
                toast.success("Roster created successfully")
                // Notify parent component about the new roster
                if (onRosterCreated) {
                    onRosterCreated(newRoster)
                }
            }
        } catch (error) {
            console.error("Error creating roster:", error)
            toast.error(error?.message || "Failed to create roster")
            throw error
        }
    }

    const handleAdd = () => {
        if (!roster?.id) {
            toast.error("Please create a roster first")
            return
        }
        setSelectedWorker(null)
        setOpenEdit(true)
    }

    const handleEdit = (worker) => {
        setSelectedWorker(worker)
        setOpenEdit(true)
    }

    const handleSaveWorker = async (workerData) => {
        const rosterIdToUse = roster?.id || rosterId
        if (!rosterIdToUse) {
            toast.error("Roster not found")
            return
        }

        // Track if roster was approved before making changes
        const wasApproved = isApproved

        // Check if this is an edit (worker has an id) or new worker
        const isEditWorker = selectedWorker?.id

        if (isEditWorker) {
            // Update existing worker
            try {
                const response = await updateWorkerInRoster(rosterIdToUse, selectedWorker.id, workerData)
                
                if (response) {
                    toast.success("Worker updated successfully")
                    setOpenEdit(false)
                    setSelectedWorker(null)
                    await fetchRosterDetails()
                    // Mark as having changes if roster was approved
                    if (wasApproved) setHasUnsavedChanges(true)
                }
            } catch (error) {
                console.error("Error updating worker:", error)
                toast.error(error?.message || "Failed to update worker")
            }
            return
        }

        try {
            // If roster was approved, revert to draft first
            if (wasApproved) {
                await revertRosterToDraft(rosterIdToUse)
            }

            // API expects array of workers
            const response = await addWorkersToRoster(rosterIdToUse, [workerData])
            
            if (response) {
                toast.success("Worker added successfully")
                setOpenEdit(false)
                setSelectedWorker(null)
                await fetchRosterDetails()
            }
        } catch (error) {
            console.error("Error adding worker:", error)
            toast.error(error?.message || "Failed to add worker")
        }
    }

    const handleDeleteWorker = async (workerId) => {
        const rosterIdToUse = roster?.id || rosterId
        if (!rosterIdToUse) {
            toast.error("Roster not found")
            return
        }

        // Track if roster was approved before making changes
        const wasApproved = isApproved

        const confirmMessage = wasApproved 
            ? "Are you sure you want to delete this worker? This will require the roster to be re-approved."
            : "Are you sure you want to delete this worker?"

        if (!window.confirm(confirmMessage)) {
            return
        }

        try {
            // If roster was approved, revert to draft first
            if (wasApproved) {
                await revertRosterToDraft(rosterIdToUse)
            }

            await deleteWorkerFromRoster(rosterIdToUse, workerId)
            toast.success("Worker deleted successfully")
            await fetchRosterDetails()
        } catch (error) {
            console.error("Error deleting worker:", error)
            toast.error(error?.message || "Failed to delete worker")
        }
    }

    const handleUpdateRosterName = async () => {
        const rosterIdToUse = roster?.id || rosterId
        if (!rosterIdToUse) {
            toast.error("Roster not found")
            return
        }

        if (!editedRosterName.trim()) {
            toast.error("Roster name cannot be empty")
            return
        }

        try {
            const response = await updateRoster(rosterIdToUse, editedRosterName.trim())
            if (response) {
                toast.success("Roster name updated successfully")
                setIsEditingRosterName(false)
                // Refresh roster to get updated name
                await fetchRosterDetails()
            }
        } catch (error) {
            console.error("Error updating roster:", error)
            toast.error(error?.message || "Failed to update roster name")
        }
    }

    // Parse CSV content into array of worker objects
    const parseCSV = (csvText) => {
        const lines = csvText.split('\n').map(line => line.trim()).filter(line => line)
        if (lines.length < 2) {
            throw new Error("CSV must have a header row and at least one data row")
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
        
        // Map common header variations to our expected fields
        const headerMap = {
            'firstname': 'firstName',
            'first_name': 'firstName',
            'first name': 'firstName',
            'lastname': 'lastName',
            'last_name': 'lastName',
            'last name': 'lastName',
            'phonenumber': 'phoneNumber',
            'phone_number': 'phoneNumber',
            'phone number': 'phoneNumber',
            'phone': 'phoneNumber',
            'nationalid': 'nationalId',
            'national_id': 'nationalId',
            'national id': 'nationalId',
            'id': 'nationalId',
            'nationality': 'nationality',
            'speciality': 'speciality',
            'specialty': 'speciality',
            'role': 'speciality',
            'job': 'speciality',
        }

        const mappedHeaders = headers.map(h => headerMap[h] || h)
        const workers = []
        const errors = []

        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i])
            const worker = {}
            
            mappedHeaders.forEach((header, index) => {
                if (['firstName', 'lastName', 'phoneNumber', 'nationalId', 'nationality', 'speciality'].includes(header)) {
                    let value = values[index]?.trim() || ''
                    
                    // Handle scientific notation from Excel (e.g., 3.2131E+10 -> 32131000000)
                    if (value && (header === 'phoneNumber' || header === 'nationalId')) {
                        if (/^[\d.]+[eE][+\-]?\d+$/.test(value)) {
                            value = Number(value).toFixed(0)
                        }
                        // Remove any non-digit characters except + for phone
                        if (header === 'phoneNumber') {
                            value = value.replace(/[^\d+]/g, '')
                        }
                    }
                    
                    worker[header] = value
                }
            })

            // Validate required fields
            if (!worker.firstName || !worker.lastName) {
                errors.push({ row: i + 1, reason: "Missing firstName or lastName" })
                continue
            }

            workers.push(worker)
        }

        return { workers, errors }
    }

    // Parse a single CSV line handling quoted values
    const parseCSVLine = (line) => {
        const result = []
        let current = ''
        let inQuotes = false

        for (let i = 0; i < line.length; i++) {
            const char = line[i]
            if (char === '"') {
                inQuotes = !inQuotes
            } else if (char === ',' && !inQuotes) {
                result.push(current)
                current = ''
            } else {
                current += char
            }
        }
        result.push(current)
        return result
    }

    const handleCSVUpload = async (event) => {
        const file = event.target.files?.[0]
        if (!file) return

        // Validate file type
        if (!file.name.endsWith('.csv')) {
            toast.error("Please upload a CSV file")
            event.target.value = "" // Reset file input
            return
        }

        if (!roster?.id) {
            toast.error("Please create a roster first")
            event.target.value = ""
            return
        }

        setUploadingCSV(true)
        try {
            // Read and parse CSV client-side
            const text = await file.text()
            const { workers: parsedWorkers, errors } = parseCSV(text)

            if (parsedWorkers.length === 0) {
                toast.error("No valid workers found in CSV")
                return
            }

            // Show parsing errors if any
            if (errors.length > 0) {
                const errorMessages = errors.slice(0, 3).map(err => `Row ${err.row}: ${err.reason}`).join(", ")
                toast.warning(`Some rows skipped: ${errorMessages}${errors.length > 3 ? '...' : ''}`)
                }

            // Add workers one by one using existing API
            let addedCount = 0
            let failedCount = 0

            for (const worker of parsedWorkers) {
                try {
                    await addWorkersToRoster(roster.id, [worker])
                    addedCount++
                } catch (err) {
                    console.error("Failed to add worker:", worker, err)
                    failedCount++
                }
            }

            // Track if roster was approved before making changes
            const wasApproved = isApproved
            
            // If roster was approved and we added workers, revert to draft
            if (wasApproved && addedCount > 0) {
                await revertRosterToDraft(roster.id)
            }

            if (addedCount > 0) {
                toast.success(`Successfully added ${addedCount} worker(s) to roster`)
                await fetchRosterDetails()
            }
            
            if (failedCount > 0) {
                toast.warning(`${failedCount} worker(s) failed to add`)
            }
        } catch (error) {
            console.error("Error uploading CSV:", error)
            toast.error(error?.message || "Failed to upload CSV file")
        } finally {
            setUploadingCSV(false)
            event.target.value = "" // Reset file input
        }
    }

    // Generate CSV template (just headers)
    const handleDownloadTemplate = () => {
        const headers = ["firstName", "lastName", "phoneNumber", "nationalId", "nationality", "speciality"]
        const csvContent = headers.join(",") + "\n"
        
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = "roster_template.csv"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        toast.success("CSV template downloaded")
    }

    // Handle share roster with Reno (submit for approval)
    const handleShareWithReno = async () => {
        const rosterIdToUse = roster?.id || rosterId
        if (!rosterIdToUse) {
            toast.error("Roster not found")
            return
        }

        const workers = roster?.workers || []
        if (workers.length === 0) {
            toast.error("Please add at least one worker before sharing")
            return
        }

        const message = hasUnsavedChanges 
            ? "You have made changes to the approved roster. Submit these changes for re-approval?"
            : "Are you sure you want to share this roster with Reno for approval? You won't be able to make changes until it's reviewed."

        if (!window.confirm(message)) {
            return
        }

        setSubmittingRoster(true)
        try {
            const response = await submitRosterForApproval(rosterIdToUse)
            if (response) {
                toast.success("Roster submitted for approval")
                await fetchRosterDetails(true) // Reset changes flag after submission
            }
        } catch (error) {
            console.error("Error submitting roster:", error)
            toast.error(error?.message || "Failed to submit roster for approval")
        } finally {
            setSubmittingRoster(false)
        }
    }

    // Handle CSV upload when no roster exists - create roster first, then upload
    const handleCSVUploadNoRoster = async (event) => {
        const file = event.target.files?.[0]
        if (!file) return

        if (!file.name.endsWith('.csv')) {
            toast.error("Please upload a CSV file")
            event.target.value = ""
            return
        }

        // First, create a roster
        if (!projectId) {
            toast.error("Project ID is missing")
            event.target.value = ""
            return
        }

        setUploadingCSV(true)
        try {
            // Create roster first
            const rosterResponse = await createProjectRoster(projectId, "Project Roster")
            const newRoster = rosterResponse.data || rosterResponse
            
            if (!newRoster?.id) {
                throw new Error("Failed to create roster")
            }

            // Update local state
            setRoster(newRoster)
            
            // Notify parent
            if (onRosterCreated) {
                onRosterCreated(newRoster)
            }

            // Now parse and upload CSV
            const text = await file.text()
            const { workers: parsedWorkers, errors } = parseCSV(text)

            if (parsedWorkers.length === 0) {
                toast.error("No valid workers found in CSV")
                return
            }

            if (errors.length > 0) {
                const errorMessages = errors.slice(0, 3).map(err => `Row ${err.row}: ${err.reason}`).join(", ")
                toast.warning(`Some rows skipped: ${errorMessages}${errors.length > 3 ? '...' : ''}`)
            }

            let addedCount = 0
            let failedCount = 0

            for (const worker of parsedWorkers) {
                try {
                    await addWorkersToRoster(newRoster.id, [worker])
                    addedCount++
                } catch (err) {
                    console.error("Failed to add worker:", worker, err)
                    failedCount++
                }
            }

            if (addedCount > 0) {
                toast.success(`Roster created with ${addedCount} worker(s)`)
                await fetchRosterDetails()
            }
            
            if (failedCount > 0) {
                toast.warning(`${failedCount} worker(s) failed to add`)
            }
        } catch (error) {
            console.error("Error:", error)
            toast.error(error?.message || "Failed to create roster and upload CSV")
        } finally {
            setUploadingCSV(false)
            event.target.value = ""
        }
    }

    // Show create roster CTA if no roster exists (only if no rosterId was provided)
    if (!rosterId && !roster) {
        return (
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E4E7EC",
                    borderRadius: "8px",
                    p: 3,
                    textAlign: "center",
                }}
            >
                <Typography sx={{ fontWeight: 600, color: "#101828", fontSize: "22px", mb: 2 }}>
                    Project Roster
                </Typography>
                <Typography sx={{ color: "#667085", fontSize: "14px", mb: 3 }}>
                    No roster found. Create a roster to start managing workers.
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreateRoster(true)}
                    sx={{
                        backgroundColor: "#4D6BF6",
                        textTransform: "none",
                        fontSize: "14px",
                        fontWeight: 500,
                        "&:hover": { backgroundColor: "#3D5BD6" },
                    }}
                >
                    Create Roster
                </Button>

                <CreateRosterModal
                    open={openCreateRoster}
                    onClose={() => setOpenCreateRoster(false)}
                    onCreate={handleCreateRoster}
                />
            </Paper>
        )
    }

    const workers = roster?.workers || []

    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid #E4E7EC",
                borderRadius: "8px",
                p: 3,
            }}
        >
            {/* Worker Search Section */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 600, color: "#101828", fontSize: "16px", mb: 1 }}>
                    Search Workers
                </Typography>
                <WorkerSearch
                    projectId={projectId}
                    onWorkerSelect={(worker) => {
                        setSelectedWorkerForSchedule(worker)
                        setOpenWorkerSchedule(true)
                    }}
                />
            </Box>

            {/* Header Row */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: "#101828", fontSize: "22px" }}>
                        Project Roster
                    </Typography>
                        <Chip 
                            size="small" 
                            {...getStatusChipProps()} 
                            icon={isLocked ? <LockOutlinedIcon sx={{ fontSize: 14 }} /> : undefined}
                        />
                    </Box>
                    {roster?.rosterName && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            {isEditingRosterName ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <TextField
                                        size="small"
                                        value={editedRosterName}
                                        onChange={(e) => setEditedRosterName(e.target.value)}
                                        sx={{
                                            width: "300px",
                                            "& .MuiOutlinedInput-root": {
                                                height: "32px",
                                                fontSize: "14px",
                                            },
                                        }}
                                        autoFocus
                                    />
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleUpdateRosterName}
                                        sx={{
                                            backgroundColor: "#4D6BF6",
                                            textTransform: "none",
                                            fontSize: "12px",
                                            height: "32px",
                                            "&:hover": { backgroundColor: "#3D5BD6" },
                                        }}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            setIsEditingRosterName(false)
                                            setEditedRosterName(roster.rosterName)
                                        }}
                                        sx={{
                                            textTransform: "none",
                                            fontSize: "12px",
                                            height: "32px",
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                        {roster.rosterName}
                                    </Typography>
                                    {!isLocked && (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditedRosterName(roster.rosterName)
                                            setIsEditingRosterName(true)
                                        }}
                                        sx={{ padding: "4px" }}
                                    >
                                        <EditOutlinedIcon sx={{ fontSize: 16, color: "#667085" }} />
                                    </IconButton>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    {/* CSV Template Download */}
                    <Button
                        variant="outlined"
                        startIcon={<DownloadOutlinedIcon />}
                        onClick={handleDownloadTemplate}
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            color: "#101828",
                            borderColor: "#E4E7EC",
                            "&:hover": { borderColor: "#D0D5DD" },
                        }}
                    >
                        CSV Template
                    </Button>
                    {/* CSV Upload */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept=".csv"
                        style={{ display: "none" }}
                        onChange={handleCSVUpload}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<UploadOutlinedIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingCSV || loading || !roster?.id || isLocked}
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            color: "#101828",
                            borderColor: "#E4E7EC",
                            "&:hover": { borderColor: "#D0D5DD" },
                        }}
                    >
                        {uploadingCSV ? "Uploading..." : "Upload CSV"}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        disabled={loading || isLocked}
                        sx={{
                            backgroundColor: "#4D6BF6",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 500,
                            "&:hover": { backgroundColor: "#3D5BD6" },
                            "&:disabled": { backgroundColor: "#E4E7EC", color: "#667085" },
                        }}
                    >
                        Add Worker
                    </Button>
                    {/* Share with Reno button - show for draft/rejected OR approved with changes */}
                    {needsResubmit && (roster?.workers?.length || 0) > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<ShareIcon />}
                            onClick={handleShareWithReno}
                            disabled={submittingRoster || loading}
                            sx={{
                                backgroundColor: "#12B76A",
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                "&:hover": { backgroundColor: "#0E9F5C" },
                            }}
                        >
                            {submittingRoster ? "Submitting..." : (hasUnsavedChanges ? "Resubmit for Approval" : "Share with Reno")}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Approved roster with changes notice */}
            {isApproved && hasUnsavedChanges && (
                <Box
                    sx={{
                        backgroundColor: "#FFF7ED",
                        border: "1px solid #FDBA74",
                        borderRadius: "8px",
                        p: 2,
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <Typography sx={{ color: "#9A3412", fontSize: "14px" }}>
                        You have made changes to this approved roster. Click "Resubmit for Approval" to send for review.
                    </Typography>
                </Box>
            )}

            {/* Approved roster notice (no changes) */}
            {isApproved && !hasUnsavedChanges && (
                <Box
                    sx={{
                        backgroundColor: "#F0FDF4",
                        border: "1px solid #BBF7D0",
                        borderRadius: "8px",
                        p: 2,
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <LockOutlinedIcon sx={{ color: "#16A34A", fontSize: 20 }} />
                    <Typography sx={{ color: "#166534", fontSize: "14px" }}>
                        This roster has been approved. Any changes will require re-approval from Reno.
                    </Typography>
                </Box>
            )}

            {/* Pending approval notice */}
            {isSubmitted && (
                <Box
                    sx={{
                        backgroundColor: "#FFF7ED",
                        border: "1px solid #FDBA74",
                        borderRadius: "8px",
                        p: 2,
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <LockOutlinedIcon sx={{ color: "#C4320A", fontSize: 20 }} />
                    <Typography sx={{ color: "#9A3412", fontSize: "14px" }}>
                        This roster is pending approval from Reno. You cannot make changes until it's reviewed.
                    </Typography>
                </Box>
            )}

            {/* Rejected notice */}
            {isRejected && (
                <Box
                    sx={{
                        backgroundColor: "#FEF2F2",
                        border: "1px solid #FECACA",
                        borderRadius: "8px",
                        p: 2,
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <Typography sx={{ color: "#991B1B", fontSize: "14px" }}>
                        This roster was rejected. Please make the necessary changes and resubmit for approval.
                    </Typography>
                </Box>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Table Header */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1.5fr 1.5fr 1.2fr 2fr 1fr 1fr 1fr",
                            borderBottom: "1px solid #E4E7EC",
                            pb: 1,
                            color: "#667085",
                            fontSize: "14px",
                        }}
                    >
                        <Typography sx={{ fontSize: "14px" }}>First Name</Typography>
                        <Typography sx={{ fontSize: "14px" }}>Last Name</Typography>
                        <Typography sx={{ fontSize: "14px" }}>Phone Number</Typography>
                        <Typography sx={{ fontSize: "14px" }}>National ID</Typography>
                        <Typography sx={{ fontSize: "14px" }}>Nationality</Typography>
                        <Typography sx={{ fontSize: "14px" }}>Speciality</Typography>
                        <Typography sx={{ fontSize: "14px" }}>Actions</Typography>
                    </Box>

                    {/* Table Rows */}
                    {workers.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: "center" }}>
                            <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                No workers added yet. Click "Add Worker" to get started.
                            </Typography>
                        </Box>
                    ) : (
                        workers.map((w, index) => (
                            <Box
                                key={w.id || index}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1.5fr 1.5fr 1.2fr 2fr 1fr 1fr 1fr",
                                    alignItems: "center",
                                    py: 1.5,
                                    borderBottom:
                                        index !== workers.length - 1 ? "1px solid #E4E7EC" : "none",
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: "#101828", fontSize: "14px" }}>
                                    {w.firstName || ""}
                                </Typography>
                                <Typography sx={{ color: "#101828", fontSize: "14px" }}>
                                    {w.lastName || ""}
                                </Typography>
                                <Typography sx={{ color: "#101828", fontSize: "14px" }}>
                                    {w.phoneNumber || ""}
                                </Typography>
                                <Typography sx={{ color: "#101828", fontSize: "14px" }}>
                                    {w.nationalId || ""}
                                </Typography>
                                <Typography sx={{ color: "#101828", fontSize: "14px" }}>
                                    {w.nationality || ""}
                                </Typography>
                                <Typography sx={{ color: "#101828", fontSize: "14px" }}>
                                    {w.speciality || ""}
                                </Typography>
                                                <Box sx={{ display: "flex", gap: 1 }}>
                                                    <IconButton
                                        size="small" 
                                        onClick={() => handleDeleteWorker(w.id)}
                                        title="Delete worker"
                                        disabled={isLocked}
                                    >
                                        <DeleteOutlineOutlinedIcon 
                                            sx={{ 
                                                fontSize: 18, 
                                                color: isLocked ? "#D0D5DD" : "#101828"
                                            }} 
                                        />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))
                    )}

                    {/* Summary */}
                    {workers.length > 0 && (
                        <Typography sx={{ mt: 2, fontSize: "13px", color: "#667085" }}>
                            Total Workers: {workers.length}
                        </Typography>
                    )}
                </>
            )}

            {/* Worker Modal */}
            <WorkerModal
                open={openEdit}
                onClose={() => {
                    setOpenEdit(false)
                    setSelectedWorker(null)
                }}
                worker={selectedWorker}
                onSave={handleSaveWorker}
            />

            {/* Create Roster Modal */}
            <CreateRosterModal
                open={openCreateRoster}
                onClose={() => setOpenCreateRoster(false)}
                onCreate={handleCreateRoster}
            />

            {/* Worker Schedule Modal */}
            <WorkerScheduleModal
                open={openWorkerSchedule}
                onClose={() => {
                    setOpenWorkerSchedule(false)
                    setSelectedWorkerForSchedule(null)
                }}
                worker={selectedWorkerForSchedule}
                projectId={projectId}
            />
        </Paper>
    )
}
